<template>
	<div>
		<bee-delete-dialog
			v-model="deleteDialog"
			moduleType="sliders"
			:ids="deleteItemId"
			@on-delete="fetchSliders"
		>
			{{$t('are-you-sure-you-want-to')}} 
			<span class="text-uppercase error--text">{{$t('delete')}}</span> 
			{{$t('Sliders.the-slider')}}
		</bee-delete-dialog>

		<!-- add/edit dialog -->
		<v-dialog
			v-model="dialog"
			width="400"
			:persistent="submitLoading"
		>
			<v-form ref="form" @submit.prevent="submit">
				<v-card class="rounded-medium">
					<v-card-title class="justify-center text-uppercase text-h6 mb-3">
						{{ editItemId ? $t('edit') : $t('add')}} {{$t('Sliders.slider')}}
					</v-card-title>
					<v-card-text class="pb-2">
						<v-row class="flex-column mt-0">
							<v-col class="pt-0 pb-6">
								<bee-drop-img
									v-model="image"
									dashed
									border-width="2"
									:border-color="isSubmitted & !image ? '#f00' : '#ccc'"
									radius="11"
									height="180px"
									blurable
									clearable
									:disabled="submitLoading"
								>
									<template v-slot:drag-text>
										<span class="text-body-1 font-weight-medium">
											{{$t('drag-text')}}
										</span>
										<span class="text-caption mb-0">
											{{$t('supported-text')}}
										</span>
									</template>
								</bee-drop-img>
							</v-col>
							<v-col class="py-0">
								<v-text-field
									v-model="description"
									:label="$t('inputs.description')"
									outlined
									dense
									class="rounded-small"
									:rules="rules.required"
								></v-text-field>
							</v-col>
							<v-col class="py-0">
								<v-text-field
									v-model="link"
									:label="$t('inputs.link')"
									outlined
									dense
									class="rounded-small"
								></v-text-field>
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-actions>
						<v-spacer/>
						<v-btn
							type="submit"
							color="primary"
							class="rounded-small"
							:loading="submitLoading"
							:disabled="submitLoading"
							@click="isSubmitted = true"
						>
							{{$t('save')}}
						</v-btn>
						<v-btn
							@click="dialog = false; editItemId = null"
							class="rounded-small"
							:disabled="submitLoading"
						>
							{{$t('cancel')}}
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>

		<!-- filters -->
		<v-card class="rounded-medium mb-3">
			<v-card-text class="pa-3">
				<v-row class="align-center">
					<!-- add -->
					<v-col md="8" cols="12" class="text-sm-start text-center">
						<v-btn
							dark
							color="darkGreen"
							class="rounded-small"
							@click="dialog = true"
						>
							<v-icon>mdi-plus</v-icon>
                            {{$t('Sliders.add-new-slider')}}
						</v-btn>
					</v-col>
					
					<!-- search -->
					<v-col md="4" cols="12" class="d-flex flex-sm-row flex-column align-center text-md-start text-center">
						<v-text-field
							v-model="search"
							:label="$t('search')"
							hide-details
							outlined
							dense
							prepend-inner-icon="mdi-magnify"
							class="rounded-medium align-self-stretch me-sm-6 mb-sm-0 mb-5"
						></v-text-field>
						<v-btn
							:color="isFiltered ? null : 'primary'"
							class="rounded-small"
							@click="isFiltered = !isFiltered"
						>
							{{isFiltered ? $t('cancel') : $t('search')}}
						</v-btn>
					</v-col>
				</v-row>
			</v-card-text>
		</v-card>

		<!-- table -->
		<v-data-table
			:headers="headers"
			:items="loading ? [] : filteredSliders"
			:loading="loading"
			:items-per-page="15"
			class="elevation-3 rounded-medium"
		>
			<!-- image -->
			<template v-slot:item.image="{ item }">
				<v-img
					:src="item.image"
					lazy-src="../assets/placeholder-image.png"
					height="200"
					min-width="200"
					max-width="600"
					transition="scale-transition"
					class="rounded-medium my-2 mx-auto"
				/>
			</template>

			<!-- Link -->
			<template v-slot:item.url="{ item }">
				<a v-if="item.url" target="_blank" :href="item.url"> {{item.url}} </a>
				<span v-else>-</span>
			</template>

			<!-- acitons -->
			<template v-slot:item.actions="{ item }">
				<div class="d-flex justify-center">
					<v-btn
						color="blue darken-4"
						dark
						small
						class="me-1 px-1 flex-shrink-1"
						min-width="20px"
						@click="editItemId = item.id; dialog = true;"
					><v-icon>mdi-pencil</v-icon></v-btn>
					<v-btn
						color="red darken-1"
						dark
						small
						class="px-1"
						min-width="20px"
						@click="deleteItemId = item.id; deleteDialog = true"
					>
						<v-icon>mdi-delete</v-icon>
					</v-btn>
				</div>
			</template>
		</v-data-table>
	</div>
</template>

<script>
import rules from '@/helpers/validation rules'
import { mapGetters, mapState } from 'vuex'

export default {
	name: 'Products',
	data: () => ({
		deleteDialog: false,
		deleteItemId: null,
		editItemId: null,
		dialog: false,

		// filter
		search: null,
		isFiltered: null,

		// helpers
		loading: false,
		submitLoading: false,
		isSubmitted: false,
		rules,

		// data
		image: null, 
		description: null,
		link: null,
	}),

	watch: {
		dialog(val) {
			if (val) {
				if (this.editItemId) {
					const item = this.getSlideById(this.editItemId);
					this.image = item.image;
					this.description = item.description;
					this.link = item.url;
				}
			} else {
				this.editItemId = null;
				this.image = null;
				this.description = null;
				this.link = null;
				this.isSubmitted = null;
				this.$nextTick(() => {
					this.$refs.form.resetValidation();
				})
			}
		}
	},

	computed: {
		...mapState({
			sliders: state => state.sliders.sliders,
		}),
		...mapGetters({
			getSlideById: 'sliders/getSlideById'
		}),

		headers() {
			return [
				{ text: this.$t('headers.image'), value: 'image', align: 'center' },
				{ text: this.$t('headers.description'), value: 'description', align: 'center', sortable: false },
				{ text: this.$t('headers.link'), value: 'url', align: 'center' },
				{ text: this.$t('headers.actions'), value: 'actions', align: 'center' },
			]
		},

		filteredSliders() {
			return this.filterByName(this.isFiltered, this.sliders, this.search, 'title')
		},

		items() {
			const items = [];
			for (let i = 0; i < 15; i++) {
				items.push({
					id: i, 
					image: 'https://www.metoffice.gov.uk/binaries/content/gallery/metofficegovuk/hero-images/advice/maps-satellite-images/satellite-image-of-globe.jpg',
					description: 'description ' + i,
					url: 'http://www.facebook.com',
				})
			}
			return items;
		}
	},
	methods: {
		submit() {
			if (this.$refs.form.validate() && this.image) {
				const actionName = this.editItemId ? 'update' : 'create';
				this.submitLoading = true;
				this.$store.dispatch(`sliders/${actionName}`, {
					id: this.editItemId,
					image: typeof this.image !== 'string' ? this.image : null,
					description: this.description,
					url: this.link,
				}).then(() => {
					const message =
						this.editItemId
							? this.$t('$-is-updated-successfully', { name: this.$t('Sliders.slider') })
							: this.$t('$-is-created-successfully', { name: this.$t('Sliders.slider') });
					this.$eventBus.$emit('show-snackbar', false, message);
					this.dialog = false;
					this.fetchSliders();
				}).finally(() => {
					this.submitLoading = false;
				})
			}
		},
		
		fetchSliders() {
			this.loading = true;
			this.$store.dispatch('sliders/fetchAll')
				.finally(() => {
					this.loading = false;
				})
		}
	},

	created() {
		this.fetchSliders();
	}	
}
</script>

<style>
</style>